import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import HotelIcon from '@mui/icons-material/Hotel';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import HotelListRow from 'components/HotelListRow';

import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';
import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';

import Query, {
  HotelListRefetchQuery,
} from '__generated__/HotelListRefetchQuery.graphql';
import { HotelList_data$key } from '__generated__/HotelList_data.graphql';
import FabDialog from 'components/FabDialog';
import HotelListAdd from 'components/HotelListAdd';

type Props = {
  data: HotelList_data$key;
};

const HotelList: FC<Props> = props => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [data, { refetch, loading }] = useAvoidSuspense<
    HotelListRefetchQuery,
    HotelList_data$key
  >(
    graphql`
      fragment HotelList_data on Query
      @refetchable(queryName: "HotelListRefetchQuery") {
        hotels(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id
              ...HotelListRow_hotel
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  const connectionId = data.hotels?.__id;

  const [deleteHotel, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = data.hotels ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<HotelIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableHeadCell center name="source">
              Šaltinis
            </TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <HotelListRow key={node.id} hotel={node} onDelete={deleteHotel} />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm">
          <HotelListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default HotelList;
